import { StyleSheet } from 'react-native';
import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, SxProps, Theme } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme, ColorScheme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';

interface ICustomSegmentedControl {
    onHandleClick: (tab: string, index: number) => void;
    tabs: string[];
    sx?: SxProps<Theme>;
    tabStyle?: React.CSSProperties;
    buttonStyle?: React.CSSProperties;
    initialIndex?: number;
    additionalDetails?: any[];
    /** `underline` (default) or filled `pill` track. */
    variant?: 'underline' | 'pill';
}

const ShortSegmentedControl: React.FC<ICustomSegmentedControl> = ({
    onHandleClick,
    tabs,
    sx,
    tabStyle,
    buttonStyle,
    initialIndex,
    additionalDetails,
    variant = 'underline',
}) => {
    const { themeStyle } = useThemeContext();
    const styles = dynamicStyles(themeStyle);
    const [currentIndex, setCurrentIndex] = useState(initialIndex ?? 0);
    const isPill = variant === 'pill';

    const handlePress = (tab: string, index: number) => {
        onHandleClick(tab, index);
        setCurrentIndex(index);
    };

    useEffect(() => {
        if (typeof initialIndex === 'number') {
            setCurrentIndex(initialIndex);
        }
    }, [initialIndex]);

    return (
        <Box
            style={styles.navbar}
            sx={{
                justifyContent: { xs: 'center', sm: 'flex-start' },
                ...(isPill
                    ? { borderTop: 'none', borderBottom: 'none', pt: 0 }
                    : {
                          borderBottom: `0.5px solid ${theme[themeStyle].light_gray_text}`,
                          borderTop: `0.5px solid ${theme[themeStyle].darkGray60}`,
                      }),
                ...sx,
            }}>
            <Box
                style={{ ...styles.flexRow, ...tabStyle }}
                sx={
                    isPill
                        ? {
                              display: 'inline-flex',
                              p: '3px',
                              borderRadius: '999px',
                              bgcolor: 'rgba(255,255,255,0.06)',
                              border: `1px solid rgba(255,255,255,0.1)`,
                              gap: '2px',
                              width: '100%',
                          }
                        : undefined
                }>
                {tabs.map((tab, index) => {
                    const selected = currentIndex === index;
                    const accent =
                        theme[themeStyle].primary_white_orange || theme[themeStyle].highlight_to_white || '#ffc965';
                    return (
                        <Button
                            onClick={() => handlePress(tab, index)}
                            key={index}
                            disableElevation
                            style={
                                isPill
                                    ? {
                                          ...styles.navButton,
                                          ...buttonStyle,
                                          backgroundColor: selected ? accent : 'transparent',
                                          borderRadius: 999,
                                      }
                                    : { ...styles.navButton, ...buttonStyle }
                            }
                            sx={
                                isPill
                                    ? {
                                          textTransform: 'none',
                                          flex: 1,
                                          minHeight: 36,
                                          py: 0.75,
                                          px: 2,
                                          borderRadius: '999px !important',
                                          bgcolor: selected ? `${accent} !important` : 'transparent !important',
                                          color: selected ? '#1a1200' : theme[themeStyle].form_label,
                                          boxShadow: selected ? '0 1px 6px rgba(0,0,0,0.35)' : 'none',
                                          transition: 'background-color 0.2s ease, color 0.2s ease',
                                          '&:hover': {
                                              bgcolor: selected
                                                  ? `${accent} !important`
                                                  : 'rgba(255,255,255,0.08) !important',
                                          },
                                      }
                                    : {
                                          textTransform: 'none',
                                          borderBottom: selected
                                              ? `1px solid ${theme[themeStyle].primary_orange}`
                                              : 'none',
                                          transition: 'all 0.3 ease-in-out',
                                      }
                            }>
                            <Typography
                                style={{ ...styles.navText, display: 'inline' }}
                                sx={{
                                    color: isPill
                                        ? selected
                                            ? '#1a1200'
                                            : theme[themeStyle].form_label
                                        : selected
                                        ? theme[themeStyle].primary_white
                                        : theme[themeStyle].form_label,
                                    fontWeight: selected ? 700 : 600,
                                    transition: 'all 0.2s ease-in-out',
                                }}>
                                {tab}{' '}
                                {additionalDetails && additionalDetails[index] && (
                                    <Typography
                                        style={{
                                            display: 'inline',
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 10,
                                            color: theme[themeStyle].grey,
                                        }}
                                        sx={{
                                            transition: 'all 0.3 ease-in-out',
                                        }}>
                                        {additionalDetails[index]}
                                    </Typography>
                                )}
                            </Typography>
                        </Button>
                    );
                })}
            </Box>
        </Box>
    );
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        text: {
            textAlign: 'left',
            fontSize: 24,
            fontStyle: 'normal',
            fontWeight: '600',
            color: theme[colorScheme].filters_white,
            fontFamily: FONT_ENUM.MAIN_BOLD,
        },
        navbar: {
            display: 'flex',
            alignItems: 'center',
            paddingTop: 10,
        },
        flexRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        navButton: {
            backgroundColor: theme[colorScheme].transparent,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 0,
        },
        navText: {
            textAlign: 'center',
            fontSize: 14,
            fontStyle: 'normal',
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_BOLD,
            textTransform: 'capitalize',
        },
    });

ShortSegmentedControl.defaultProps = {
    tabs: ['tab1', 'tab2', 'tab3'],
};

export { ShortSegmentedControl };
